import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Chip, Tabs, Tab } from '@nextui-org/react';
import ModalSlideFilter from '../../../../common/src/components/filters/modal-sidebar-filter';
import useDataStore from '../../../../common/src/stores/useDataStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import Header from '../../components/layouts/Header';
import { FiltersToggle, LoadingState, PageLayout } from '../../components/ui';
// Insights tab oculta por ahora (pedido 2026-07) — no eliminar.
// import InsightsTab from './InsightsTab';
import DailyTab from './DailyTab';
import EvolutionTab from './EvolutionTab';
import DataTableTab from './DataTableTab';
import USDAFilters from './USDAFilters';
import type { ContentTab, DataSource, UnitMode } from './types';
import type { WholesaleRecord, RetailRecord, MovementRecord } from './types';
import {
  SPECIES_TO_USDA_COMMODITY,
  TERMINAL_REPORTS,
  SHIPPING_REPORTS,
  MOVEMENT_REPORTS,
  RETAIL_SLUG,
  getSeasonDates,
  fetchMultipleReports,
  fetchReport,
  shortMarket,
  parseUSDate,
} from './utils';

// Re-export types and utils for InsightsTab backward compatibility
export type { WholesaleRecord, RetailRecord } from './types';
export { parseUSDate, fmtDate, shortMarket, parsePackageLbs } from './utils';

const USDAMarketExplorer = () => {
  const { t } = useTranslation('usda-market');
  const { selectedSpecies } = useDataStore();
  const { selectedSeason } = useFilterStore();

  const commodity = selectedSpecies?.excel_name
    ? SPECIES_TO_USDA_COMMODITY[selectedSpecies.excel_name.toUpperCase()] || 'Cherries'
    : 'Cherries';
  const initialDates = getSeasonDates(selectedSeason) || { from: '01/01/2026', to: '02/21/2026' };

  // Layout state
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [activeTab, setActiveTab] = useState<ContentTab>('evolution');

  // Data source & unit
  const [dataSource, setDataSource] = useState<DataSource>('wholesale');
  const [unit, setUnit] = useState<UnitMode>('kg');

  // Date range
  const [dateRange, setDateRange] = useState<[Date, Date]>([
    parseUSDate(initialDates.from),
    parseUSDate(initialDates.to),
  ]);

  // Loading states per source
  const [loadingWholesale, setLoadingWholesale] = useState(false);
  const [loadingFob, setLoadingFob] = useState(false);
  const [loadingRetail, setLoadingRetail] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState('');

  // Raw data
  const [terminalData, setTerminalData] = useState<WholesaleRecord[]>([]);
  const [shippingData, setShippingData] = useState<WholesaleRecord[]>([]);
  const [retailData, setRetailData] = useState<RetailRecord[]>([]);

  // "VS temporada": overlay the same window of a previous season on the
  // Evolution chart. When set, we fetch the current date range shifted back
  // to that season (same markets/commodity) and pass it down as comparison.
  const [vsSeason, setVsSeason] = useState<string | null>(null);
  const [comparisonData, setComparisonData] = useState<WholesaleRecord[]>([]);
  const [loadingComparison, setLoadingComparison] = useState(false);

  // Industry shipment volume (district movement reports), for the volume
  // bars on the Evolution chart. Fetched lazily — only when "Mostrar
  // volumen" is on (17 reports; the per-report cache makes repeats free).
  const [showVolume, setShowVolume] = useState(false);
  const [movementData, setMovementData] = useState<MovementRecord[]>([]);
  const [loadingMovement, setLoadingMovement] = useState(false);

  // Filters
  // Default markets: keep the initial load light — only these terminal
  // reports are fetched until the user picks others and hits "Buscar Datos".
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([
    'Los Angeles',
    'Philadelphia',
  ]);
  const [selectedVarieties, setSelectedVarieties] = useState<string[]>([]);
  const [selectedOrigins, setSelectedOrigins] = useState<string[]>([]);
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);

  // Track if any source is loading
  const isAnyLoading = loadingWholesale || loadingFob || loadingRetail;

  // Format dates for API (MM/DD/YYYY)
  const fmtAPIDate = (d: Date) =>
    `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;

  const dateFrom = fmtAPIDate(dateRange[0]);
  const dateTo = fmtAPIDate(dateRange[1]);

  // Active raw data based on source
  const activeRawData = useMemo(() => {
    if (dataSource === 'wholesale') return terminalData;
    if (dataSource === 'fob') return shippingData;
    return [];
  }, [dataSource, terminalData, shippingData]);

  // Filter options derived from active data
  const filterOptions = useMemo(() => {
    if (dataSource === 'retail') {
      const regions = new Set<string>();
      const varieties = new Set<string>();
      const sizes = new Set<string>();
      retailData.forEach((r) => {
        if (r.region) regions.add(r.region);
        if (r.variety && r.variety !== 'N/A') varieties.add(r.variety);
        if (r.size && r.size !== 'N/A') sizes.add(r.size);
      });
      return {
        markets: Array.from(regions).sort((a, b) =>
          a === 'National' ? -1 : b === 'National' ? 1 : a.localeCompare(b)
        ),
        varieties: Array.from(varieties).sort(),
        origins: [],
        packages: [],
        sizes: Array.from(sizes).sort(),
      };
    }

    const m = new Set<string>();
    const v = new Set<string>();
    const o = new Set<string>();
    const p = new Set<string>();
    const s = new Set<string>();
    // Wholesale fetches only the selected markets, so the dropdown must
    // offer every known terminal city — not just what's loaded — to let
    // the user add markets and re-search.
    if (dataSource === 'wholesale') {
      Object.values(TERMINAL_REPORTS).forEach((city) => m.add(city));
    }
    activeRawData.forEach((r) => {
      const market = shortMarket(r.market_location_name || '');
      if (market) m.add(market);
      if (r.variety && r.variety !== 'N/A') v.add(r.variety);
      if (r.origin && r.origin !== 'N/A') o.add(r.origin);
      if (r.package && r.package !== 'N/A') p.add(r.package);
      if (r.item_size && r.item_size !== 'N/A') s.add(r.item_size);
    });
    return {
      markets: Array.from(m).sort(),
      varieties: Array.from(v).sort(),
      origins: Array.from(o).sort(),
      packages: Array.from(p).sort(),
      sizes: Array.from(s).sort(),
    };
  }, [dataSource, activeRawData, retailData]);

  // Predicado de filtros, compartido por la temporada actual y la de
  // comparación: si solo se aplicara a la actual, al comparar temporadas la
  // línea vieja traería TODOS los orígenes/variedades y la comparación sería
  // inválida sin que nada lo advierta en pantalla.
  const matchesFilters = useCallback(
    (r: WholesaleRecord) => {
      const market = shortMarket(r.market_location_name || '');
      if (selectedMarkets.length > 0 && !selectedMarkets.includes(market)) return false;
      if (selectedVarieties.length > 0 && !selectedVarieties.includes(r.variety)) return false;
      if (selectedOrigins.length > 0 && !selectedOrigins.includes(r.origin)) return false;
      if (selectedPackages.length > 0 && !selectedPackages.includes(r.package)) return false;
      if (selectedSizes.length > 0 && !selectedSizes.includes(r.item_size)) return false;
      return true;
    },
    [selectedMarkets, selectedVarieties, selectedOrigins, selectedPackages, selectedSizes]
  );

  // Filtered wholesale data
  const filteredWholesaleData = useMemo(
    () => activeRawData.filter(matchesFilters),
    [activeRawData, matchesFilters]
  );

  // La temporada de comparación pasa por los mismos filtros que la actual.
  const filteredComparisonData = useMemo(
    () => comparisonData.filter(matchesFilters),
    [comparisonData, matchesFilters]
  );

  // Filtered retail data
  const filteredRetailData = useMemo(() => {
    return retailData.filter((r) => {
      if (selectedMarkets.length > 0 && !selectedMarkets.includes(r.region)) return false;
      if (selectedVarieties.length > 0 && !selectedVarieties.includes(r.variety)) return false;
      if (selectedSizes.length > 0 && !selectedSizes.includes(r.size)) return false;
      return true;
    });
  }, [retailData, selectedMarkets, selectedVarieties, selectedSizes]);

  // Clear filters only when the data source ACTUALLY changes — comparing
  // against the previous value is StrictMode-safe (a mounted-flag ref gets
  // re-run on the double mount and wipes the default market selection).
  const prevDataSource = useRef(dataSource);
  useEffect(() => {
    if (prevDataSource.current === dataSource) return;
    prevDataSource.current = dataSource;
    setSelectedMarkets([]);
    setSelectedVarieties([]);
    setSelectedOrigins([]);
    setSelectedPackages([]);
    setSelectedSizes([]);
  }, [dataSource]);

  // Abort controller ref for cancelling background fetches
  const fetchIdRef = useRef(0);

  // Progressive fetch: wholesale first (blocking), then fob + retail in background
  const fetchAll = useCallback(async () => {
    const id = ++fetchIdRef.current;

    // Clear all data
    setTerminalData([]);
    setShippingData([]);
    setRetailData([]);
    setMovementData([]);

    // Phase 1: Wholesale (blocking — shows spinner)
    // Only fetch the terminal reports for the selected markets (defaults to
    // Los Angeles + Philadelphia) — fetching all 9 cities is slow and heavy.
    const terminalReports =
      selectedMarkets.length > 0
        ? Object.fromEntries(
            Object.entries(TERMINAL_REPORTS).filter(([, city]) =>
              selectedMarkets.includes(city)
            )
          )
        : TERMINAL_REPORTS;
    const reportsToFetch =
      Object.keys(terminalReports).length > 0 ? terminalReports : TERMINAL_REPORTS;

    setLoadingWholesale(true);
    setLoadingProgress('Fetching Wholesale data...');
    try {
      const terminal = await fetchMultipleReports(reportsToFetch, commodity, dateFrom, dateTo, setLoadingProgress);
      if (id !== fetchIdRef.current) return; // stale
      setTerminalData(terminal);
    } catch {
      // skip
    }
    setLoadingWholesale(false);
    setLoadingProgress('');

    if (id !== fetchIdRef.current) return;

    // Phase 2: FOB in background
    setLoadingFob(true);
    fetchMultipleReports(SHIPPING_REPORTS, commodity, dateFrom, dateTo, () => {})
      .then((shipping) => {
        if (id !== fetchIdRef.current) return;
        setShippingData(shipping);
      })
      .catch(() => {})
      .finally(() => {
        if (id === fetchIdRef.current) setLoadingFob(false);
      });

    // Phase 3: Retail in background
    setLoadingRetail(true);
    fetchReport(RETAIL_SLUG, commodity, dateFrom, dateTo)
      .then(({ sections }) => {
        if (id !== fetchIdRef.current) return;
        const details = sections.find((s) => s.name === 'Report Details');
        if (details) setRetailData(details.results as RetailRecord[]);
      })
      .catch(() => {})
      .finally(() => {
        if (id === fetchIdRef.current) setLoadingRetail(false);
      });

  }, [commodity, dateFrom, dateTo, selectedMarkets]);

  // Lazy fetch of movement volume: runs when the user turns the toggle on
  // (or when commodity/dates change while it's on). Cached per report, so
  // toggling off/on or repeating a search doesn't re-hit the network.
  useEffect(() => {
    if (!showVolume) return;
    let cancelled = false;
    setLoadingMovement(true);
    fetchMultipleReports(MOVEMENT_REPORTS, commodity, dateFrom, dateTo, () => {})
      .then((rows) => {
        if (!cancelled) setMovementData(rows as unknown as MovementRecord[]);
      })
      .catch(() => {
        if (!cancelled) setMovementData([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingMovement(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showVolume, commodity, dateFrom, dateTo]);

  useEffect(() => {
    const dates = getSeasonDates(selectedSeason);
    if (dates) {
      setDateRange([parseUSDate(dates.from), parseUSDate(dates.to)]);
    }
  }, [selectedSeason]);

  // Season of the current date range (Jul→Jun). Use the range MIDPOINT so
  // the one-day timezone drift in season boundaries (e.g. 06/30 instead of
  // 07/01) can't misclassify the season.
  const rangeMid = new Date(
    (dateRange[0].getTime() + dateRange[1].getTime()) / 2
  );
  const currentSeasonStartYear =
    rangeMid.getMonth() >= 6 ? rangeMid.getFullYear() : rangeMid.getFullYear() - 1;
  // How many years to add to comparison dates to land on the current axis.
  const comparisonYearDiff = vsSeason
    ? currentSeasonStartYear - parseInt(vsSeason.split('-')[0], 10)
    : 0;

  // Fetch comparison season data (wholesale/FOB only) when VS is enabled.
  // Any season other than the one on screen works — later seasons may
  // already be fully in the past (diff can be negative, the shift is
  // symmetric).
  useEffect(() => {
    if (!vsSeason || comparisonYearDiff === 0 || dataSource === 'retail') {
      setComparisonData([]);
      return;
    }
    let cancelled = false;
    const shift = (d: Date) =>
      fmtAPIDate(
        new Date(d.getFullYear() - comparisonYearDiff, d.getMonth(), d.getDate())
      );
    const reports = dataSource === 'wholesale' ? TERMINAL_REPORTS : SHIPPING_REPORTS;
    const filtered =
      selectedMarkets.length > 0
        ? Object.fromEntries(
            Object.entries(reports).filter(([, city]) =>
              selectedMarkets.includes(city)
            )
          )
        : reports;
    const toFetch = Object.keys(filtered).length > 0 ? filtered : reports;

    setLoadingComparison(true);
    fetchMultipleReports(toFetch, commodity, shift(dateRange[0]), shift(dateRange[1]), () => {})
      .then((rows) => {
        if (!cancelled) setComparisonData(rows);
      })
      .catch(() => {
        if (!cancelled) setComparisonData([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingComparison(false);
      });
    return () => {
      cancelled = true;
    };
  }, [vsSeason, comparisonYearDiff, dataSource, commodity, dateFrom, dateTo, selectedMarkets]);

  useEffect(() => {
    fetchAll();
  }, [commodity]);

  // Handle date range change
  const handleDateRangeChange = useCallback((value: [Date, Date] | null) => {
    if (!value) return;
    const [start, end] = value;
    start.setHours(12, 0, 0, 0);
    end.setHours(12, 0, 0, 0);
    setDateRange([start, end]);
  }, []);

  // Determine disabled sources
  const isFobDisabled = loadingFob && shippingData.length === 0;
  const isRetailDisabled = loadingRetail && retailData.length === 0;

  const recordCount = dataSource === 'retail' ? filteredRetailData.length : filteredWholesaleData.length;
  const sourceLabel =
    dataSource === 'wholesale' ? t('Wholesale (Terminal)') :
    dataSource === 'fob' ? t('FOB (Shipping Point)') : t('Retail');

  return (
    <div className='flex w-full h-[calc(100vh-64px)] overflow-hidden'>
      {/* Sidebar Filters */}
      <ModalSlideFilter isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)}>
        <USDAFilters
          dataSource={dataSource}
          onDataSourceChange={setDataSource}
          unit={unit}
          onUnitChange={setUnit}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          onFetch={fetchAll}
          loading={loadingWholesale}
          loadingProgress={loadingProgress}
          isFobLoading={loadingFob}
          isRetailLoading={loadingRetail}
          isFobDisabled={isFobDisabled}
          isRetailDisabled={isRetailDisabled}
          filterOptions={filterOptions}
          selectedMarkets={selectedMarkets}
          selectedVarieties={selectedVarieties}
          selectedOrigins={selectedOrigins}
          selectedPackages={selectedPackages}
          selectedSizes={selectedSizes}
          onMarketsChange={setSelectedMarkets}
          onVarietiesChange={setSelectedVarieties}
          onOriginsChange={setSelectedOrigins}
          onPackagesChange={setSelectedPackages}
          onSizesChange={setSelectedSizes}
        />
      </ModalSlideFilter>

      {/* Main Content */}
      <PageLayout className='overflow-y-auto'>
        <Header
          title={t('USDA Market Explorer')}
          description={
            <>
              {commodity} — {sourceLabel} — {unit === 'kg' ? '$/kg' : '$/lb'}
              {isAnyLoading && !loadingWholesale && (
                <span className='ml-2 text-default-400'>
                  ({t('cargando')} {loadingFob ? 'FOB' : ''}{loadingFob && loadingRetail ? ' & ' : ''}{loadingRetail ? 'Retail' : ''}...)
                </span>
              )}
            </>
          }
          actions={
            <Chip size='sm' variant='flat'>
              {recordCount} {t('registros')}
            </Chip>
          }
        />
        <div className='mb-4'>
          <FiltersToggle
            isOpen={isFilterOpen}
            onToggle={() => setIsFilterOpen(!isFilterOpen)}
            label={isFilterOpen ? t('Ocultar filtros') : t('Mostrar filtros')}
          />
        </div>

        {/* Loading (only for initial wholesale fetch) */}
        {loadingWholesale && <LoadingState label={loadingProgress} />}

        {/* Content Tabs */}
        {!loadingWholesale && (
          <Tabs
            selectedKey={activeTab}
            onSelectionChange={(key) => setActiveTab(key as ContentTab)}
            variant='underlined'
            classNames={{ tabList: 'gap-6' }}
          >
            {/* Insights tab oculta por ahora (pedido 2026-07) — no eliminar.
            <Tab key='insights' title={t('Insights')}>
              <div className='pt-2'>
                <InsightsTab
                  terminalData={terminalData}
                  shippingData={shippingData}
                  retailData={retailData}
                  commodity={commodity}
                />
              </div>
            </Tab>
            */}

            <Tab key='evolution' title={t('Evolución')}>
              <EvolutionTab
                wholesaleData={filteredWholesaleData}
                retailData={filteredRetailData}
                dataSource={dataSource}
                unit={unit}
                commodity={commodity}
                vsSeason={vsSeason}
                onVsSeasonChange={setVsSeason}
                comparisonData={filteredComparisonData}
                comparisonYearDiff={comparisonYearDiff}
                loadingComparison={loadingComparison}
                currentSeasonStartYear={currentSeasonStartYear}
                showVolume={showVolume}
                onShowVolumeChange={setShowVolume}
                movementData={movementData}
                loadingMovement={loadingMovement}
              />
            </Tab>

            <Tab key='daily' title={t('Dispersión')}>
              <DailyTab
                wholesaleData={filteredWholesaleData}
                retailData={filteredRetailData}
                dataSource={dataSource}
                unit={unit}
                commodity={commodity}
              />
            </Tab>

            <Tab key='table' title={t('Tabla')}>
              <DataTableTab
                wholesaleData={filteredWholesaleData}
                retailData={filteredRetailData}
                dataSource={dataSource}
                unit={unit}
                commodity={commodity}
              />
            </Tab>
          </Tabs>
        )}
      </PageLayout>
    </div>
  );
};

export default USDAMarketExplorer;
